/**
 * TanStack Query hooks for Orders API
 *
 * Provides server state management with caching, background refetching,
 * and optimistic updates for orders.
 */

import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ordersApi } from '../services/ordersApi';
import type {
  AddCDInput,
  CreateOrderInput,
  Order,
  OrdersListParams,
  OrdersListResponse,
  OrderTransaction,
  UpdateOrderInput,
} from '../types';

/**
 * Query keys for cache management
 */
export const orderQueryKeys = {
  all: ['orders'] as const,
  lists: () => [...orderQueryKeys.all, 'list'] as const,
  list: (params?: OrdersListParams) => [...orderQueryKeys.lists(), params] as const,
  details: () => [...orderQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...orderQueryKeys.details(), id] as const,
  cds: (orderId: string) => [...orderQueryKeys.detail(orderId), 'cds'] as const,
};

// ============================================================================
// Order Queries
// ============================================================================

/**
 * Fetch list of orders with optional filters
 */
export function useOrders(
  params?: OrdersListParams,
  options?: Omit<UseQueryOptions<OrdersListResponse, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: orderQueryKeys.list(params),
    queryFn: () => ordersApi.list(params),
    ...options,
  });
}

/**
 * Fetch a single order by ID
 */
export function useOrder(
  id: string,
  options?: Omit<UseQueryOptions<Order, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: orderQueryKeys.detail(id),
    queryFn: () => ordersApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Fetch CDs (transactions) for an order
 */
export function useOrderCDs(
  orderId: string,
  options?: Omit<UseQueryOptions<OrderTransaction[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: orderQueryKeys.cds(orderId),
    queryFn: () => ordersApi.listCDs(orderId),
    enabled: !!orderId,
    ...options,
  });
}

// ============================================================================
// Order Mutations
// ============================================================================

/**
 * Create a new order
 */
export function useCreateOrder(
  options?: Omit<UseMutationOptions<Order, Error, CreateOrderInput>, 'mutationFn'>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrderInput) => ordersApi.create(data),
    onSuccess: () => {
      // Invalidate orders list to refetch
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update an order
 */
export function useUpdateOrder(
  options?: Omit<
    UseMutationOptions<Order, Error, { id: string; data: UpdateOrderInput }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrderInput }) =>
      ordersApi.update(id, data),
    onSuccess: (updatedOrder) => {
      // Update the specific order in cache
      queryClient.setQueryData(orderQueryKeys.detail(updatedOrder.id), updatedOrder);
      // Invalidate orders list to refetch
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
    },
    ...options,
  });
}

/**
 * Delete an order
 */
export function useDeleteOrder(
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ordersApi.delete(id),
    onSuccess: (_data, id) => {
      // Remove order from cache
      queryClient.removeQueries({ queryKey: orderQueryKeys.detail(id) });
      // Invalidate orders list to refetch
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.lists() });
    },
    ...options,
  });
}

/**
 * Add a CD to an order
 */
export function useAddCD(
  options?: Omit<
    UseMutationOptions<OrderTransaction, Error, { orderId: string; data: AddCDInput }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: AddCDInput }) =>
      ordersApi.addCD(orderId, data),
    onSuccess: (_data, { orderId }) => {
      // Invalidate order detail and CDs list
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.detail(orderId) });
      queryClient.invalidateQueries({ queryKey: orderQueryKeys.cds(orderId) });
    },
    ...options,
  });
}
